import { useEffect } from "react";
import { router } from "./router";

const APP_NAME = "Vidyapeeth";

// Single favicon used across the app
const appFaviconHref = "/assets/icons/vidyapeeth-logo.svg";

interface PageMetaConfig {
  title: string;
}

function getMetaForPath(pathname: string): PageMetaConfig {
  // Auth-related routes
  if (
    pathname.startsWith("/login") ||
    pathname.startsWith("/signup") ||
    pathname.startsWith("/personalize") ||
    pathname.startsWith("/account/activate") ||
    pathname.startsWith("/create-password") ||
    pathname.startsWith("/success")
  ) {
    return {
      title: `Login | ${APP_NAME}`,
    };
  }

  // Dashboard
  if (pathname === "/" || pathname.startsWith("/dashboard")) {
    return {
      title: `Dashboard | ${APP_NAME}`,
    };
  }

  // Lessons / subjects
  if (pathname.startsWith("/subjects")) {
    return {
      title: `Lessons | ${APP_NAME}`,
    };
  }

  // Tests
  if (pathname.startsWith("/tests")) {
    return {
      title: `Tests | ${APP_NAME}`,
    };
  }

  // Library
  if (pathname.startsWith("/library")) {
    return {
      title: `Library | ${APP_NAME}`,
    };
  }

  // Reports
  if (pathname.startsWith("/reports")) {
    return {
      title: `Reports | ${APP_NAME}`,
    };
  }

  // Subscriptions
  if (pathname.startsWith("/my-subscriptions")) {
    return {
      title: `Subscriptions | ${APP_NAME}`,
    };
  }

  // Tenants
  if (pathname.startsWith("/tenants")) {
    if (pathname.startsWith("/tenants/add")) {
      return { title: `Add Tenant | ${APP_NAME}` };
    }
    if (pathname.startsWith("/tenants/edit")) {
      return { title: `Edit Tenant | ${APP_NAME}` };
    }
    return { title: `Tenants | ${APP_NAME}` };
  }

  // Institutions
  if (pathname.startsWith("/institutions")) {
    if (pathname.startsWith("/institutions/add")) {
      return { title: `Add Institution | ${APP_NAME}` };
    }
    if (pathname.startsWith("/institutions/edit")) {
      return { title: `Edit Institution | ${APP_NAME}` };
    }
    return { title: `Institutions | ${APP_NAME}` };
  }

  // Fallback
  return {
    title: APP_NAME,
  };
}

function setFavicon(href: string) {
  if (typeof document === "undefined") return;
  const head = document.head || document.getElementsByTagName("head")[0];
  if (!head) return;

  let link =
    head.querySelector<HTMLLinkElement>("link[rel='icon']") ??
    head.querySelector<HTMLLinkElement>("link[rel='shortcut icon']");

  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    head.appendChild(link);
  }

  if (link.href !== href) {
    link.href = href;
  }
}

export function PageMetaManager() {
  useEffect(() => {
    // Ensure favicon is set once (and stays the same on all pages)
    setFavicon(appFaviconHref);

    const updateMeta = () => {
      const { pathname } = router.state.location;
      const { title } = getMetaForPath(pathname);

      if (typeof document !== "undefined") {
        document.title = title;
      }
    };

    // Initial run
    updateMeta();

    const unsubscribe = router.subscribe(() => {
      updateMeta();
    });

    return unsubscribe;
  }, []);

  return null;
}
